import type { Doc as BlockSuiteDoc, DocCollection } from '@blocksuite/store';

export { Doc } from './entities/doc';
export type { LoadedBlockSuiteDoc } from './entities/doc';
export type { DocMode, EdgelessModeHandler } from './entities/record';
export { DocRecord, setEdgelessModeHandler } from './entities/record';
export { DocRecordList } from './entities/record-list';
export { DocScope } from './scopes/doc';
export { DocService } from './services/doc';
export { DocsService } from './services/docs';

import type { LoadedBlockSuiteDoc } from './entities/doc';

export function isDocLoaded(doc: BlockSuiteDoc): doc is LoadedBlockSuiteDoc {
  return doc.ready === true;
}

export function assertDocLoaded(doc: BlockSuiteDoc): asserts doc is LoadedBlockSuiteDoc {
  if (!doc.ready) {
    throw new Error(`[Doc] Cannot access doc ${doc.id} - not loaded. Call doc.load() first and wait for ready.`);
  }
}

export function ensureDocLoaded(doc: BlockSuiteDoc): LoadedBlockSuiteDoc {
  if (!doc.ready) {
    doc.load();
  }
  if (!doc.ready) {
    throw new Error(`[Doc] Failed to load doc ${doc.id} - doc.ready is still false after load()`);
  }
  return doc as LoadedBlockSuiteDoc;
}

export function loadDocAsync(
  doc: BlockSuiteDoc,
  callback: (loadedDoc: LoadedBlockSuiteDoc) => void
): void {
  if (doc.ready) {
    callback(doc as LoadedBlockSuiteDoc);
    return;
  }
  doc.load(() => {
    if (doc.ready) {
      callback(doc as LoadedBlockSuiteDoc);
    }
  });
}

export function openDoc(
  docCollection: DocCollection,
  docId: string
): LoadedBlockSuiteDoc | null {
  const doc = docCollection.getDoc(docId);
  if (!doc) {
    return null;
  }
  return ensureDocLoaded(doc);
}

export function createNewDoc(
  docCollection: DocCollection,
  pageId?: string
): LoadedBlockSuiteDoc {
  const doc = docCollection.createDoc({ id: pageId });
  if (!doc) {
    throw new Error(`[Doc] Failed to create doc${pageId ? ` with id ${pageId}` : ''}`);
  }
  return ensureDocLoaded(doc);
}

import type { Framework } from '../../framework';
import {
  WorkspaceLocalState,
  WorkspaceScope,
  WorkspaceService,
} from '../workspace';
import { Doc } from './entities/doc';
import { DocRecord } from './entities/record';
import { DocRecordList } from './entities/record-list';
import { DocScope } from './scopes/doc';
import { DocService } from './services/doc';
import { DocsService } from './services/docs';
import { DocsStore } from './stores/docs';

export function configureDocModule(framework: Framework) {
  framework
    .scope(WorkspaceScope)
    .service(DocsService, [DocsStore])
    .store(DocsStore, [WorkspaceService, WorkspaceLocalState])
    .entity(DocRecord, [DocsStore])
    .entity(DocRecordList, [DocsStore])
    .scope(DocScope)
    .entity(Doc, [DocScope, DocsStore, WorkspaceService])
    .service(DocService);
}
